import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  alpha,
  useTheme,
  Grid,
  Paper,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Star,
  LocationOn,
  School,
  Public,
  Groups,
  Verified,
  ArrowForward,
} from '@mui/icons-material';
import { getTopColleges, getImageUrl } from '../../services/api';
import { scrollToSection } from '../../utils/scroll';

// ---------- College Image Mapping ----------
// Maps college names to relevant Unsplash images based on location/college
const collegeImages = {
  'Tashkent Medical Academy': 'https://images.unsplash.com/photo-1562774053-701939374585?w=400&q=80',
  'Samarkand State Medical University': 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=400&q=80',
  'Bangladesh Medical College': 'https://images.unsplash.com/photo-1587351021759-3772687fe598?w=400&q=80',
  'Kyrgyz State Medical Academy': 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&q=80',
  'Nepal Medical College': 'https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=400&q=80',
  'Moscow State Medical University': 'https://images.unsplash.com/photo-1562774053-701939374585?w=400&q=80',
  'Kasturba Medical College, Manipal': 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=400&q=80',
  'D.Y. Patil Medical College, Mumbai': 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&q=80',
  'MS Ramaiah Medical College, Bangalore': 'https://images.unsplash.com/photo-1587351021759-3772687fe598?w=400&q=80',
  'Vydehi Medical College, Bangalore': 'https://images.unsplash.com/photo-1562774053-701939374585?w=400&q=80',
  'Institute of Medical Science and SUM Hospital, Bhubaneswar': 'https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=400&q=80',
  'Kalinga Institute of Medical Science, Bhubaneswar': 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=400&q=80',
  'SRM Medical College, Chennai': 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&q=80',
  'Symbiosis Medical College for Women, Pune': 'https://images.unsplash.com/photo-1587351021759-3772687fe598?w=400&q=80',
  'Apollo Institute of Medical Science, Hyderabad': 'https://images.unsplash.com/photo-1562774053-701939374585?w=400&q=80',
  'Sarada Medical College, Noida': 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=400&q=80',
  'Amrita Medical College, Kochi': 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&q=80',
  'Datta Meghe Medical College, Wardha': 'https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=400&q=80',
  'Sri Ramachandra Medical College, Chennai': 'https://images.unsplash.com/photo-1587351021759-3772687fe598?w=400&q=80',
  'Mata Gujri Medical College, Kishanganj': 'https://images.unsplash.com/photo-1562774053-701939374585?w=400&q=80',
  'Pacific Medical College, Udaipur': 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=400&q=80',
  'KPC Medical College, Kolkata': 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&q=80',
  'Jagannath Gupta Medical College, Kolkata': 'https://images.unsplash.com/photo-1587351021759-3772687fe598?w=400&q=80',
  'ICARE Medical College, Haldia': 'https://images.unsplash.com/photo-1562774053-701939374585?w=400&q=80',
  'IQ City Medical College, Durgapur': 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=400&q=80',
  'JIS Medical College, Howrah': 'https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=400&q=80',
  'PKG Medical College, Kolkata': 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&q=80',
  'Raipur Institute of Medical Science, Raipur': 'https://images.unsplash.com/photo-1587351021759-3772687fe598?w=400&q=80',
  'St John\'s Medical College, Bangalore': 'https://images.unsplash.com/photo-1562774053-701939374585?w=400&q=80',
  'Saptagiri Institute of Medical Science, Bangalore': 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=400&q=80',
  'GITAM Institute of Medical Science, Visakhapatnam': 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&q=80',
  'Santosh Medical College, Ghaziabad': 'https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=400&q=80',
};

const getCollegeImage = (college) => {
  // If college has a custom thumbnailImage, use it
  if (college.thumbnailImage) {
    return getImageUrl(college.thumbnailImage);
  }
  // Otherwise use the mapped image or a fallback
  return collegeImages[college.name] || 'https://images.unsplash.com/photo-1562774053-701939374585?w=400&q=80';
};

// ---------- Statistics Data ----------
const statsData = [
  { icon: <School />, value: '500+', label: 'Partner Colleges' },
  { icon: <Public />, value: '15+', label: 'Countries' },
  { icon: <Groups />, value: '5000+', label: 'Students Placed' },
  { icon: <Verified />, value: '98%', label: 'Success Rate' },
];

const CollegeShowcase = () => {
  const theme = useTheme();
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    fetchTopColleges();
  }, []);

  const fetchTopColleges = async () => {
    setLoading(true);
    try {
      const response = await getTopColleges();
      setColleges(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch top colleges:', error);
      setColleges([]);
    } finally {
      setLoading(false);
    }
  };

  const scroll = useCallback((direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 340;
      const newPosition =
        direction === 'left'
          ? container.scrollLeft - scrollAmount
          : container.scrollLeft + scrollAmount;
      container.scrollTo({ left: newPosition, behavior: 'smooth' });
    }
  }, []);

  return (
    <Box sx={{ position: 'relative', bgcolor: 'background.default' }}>
      {/* ===== HERO HEADER SECTION ===== */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 6, md: 10 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, #0D2137 100%)`,
          overflow: 'hidden',
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.12)} 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -80,
            left: -80,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha('#FFFFFF', 0.04)} 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '30%',
            left: '10%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.06)} 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{
                color: '#D4A853',
                  fontWeight: 700,
                  letterSpacing: 3,
                  fontSize: '0.85rem',
                  mb: 2,
                  display: 'block',
                }}
              >
                EXPLORE INSTITUTIONS
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  color: 'white',
                  fontWeight: 800,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3.2rem' },
                  lineHeight: 1.2,
                  mb: 2,
                }}
              >
                Top Medical{' '}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(135deg, #D4A853, #E0C07A)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Colleges
                </Box>
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: alpha('#FFFFFF', 0.75),
                  fontWeight: 400,
                  maxWidth: 600,
                  lineHeight: 1.7,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                }}
              >
                Discover world-class medical institutions across 15+ countries. 
                We help you find the perfect college that matches your aspirations and budget.
              </Typography>
            </Grid>

            <Grid item xs={12} md={5}>
              {/* Stats Cards */}
              <Grid container spacing={2}>
                {statsData.map((stat, index) => (
                  <Grid item xs={6} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        bgcolor: alpha('#FFFFFF', 0.06),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha('#FFFFFF', 0.08)}`,
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: alpha('#FFFFFF', 0.1),
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box sx={{ color: '#D4A853', mb: 1, fontSize: 28 }}>
                        {stat.icon}
                      </Box>
                      <Typography
                        variant="h4"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          fontSize: { xs: '1.5rem', md: '1.8rem' },
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: alpha('#FFFFFF', 0.6), fontWeight: 500 }}
                      >
                        {stat.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== TOP COLLEGES LIST SECTION ===== */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 8 } }}>
        {/* Section Title */}
        <Box sx={{ mb: 5, textAlign: 'center' }}>
          <Typography
            variant="overline"
            sx={{
              color: '#D4A853',
              fontWeight: 700,
              letterSpacing: 2,
              fontSize: '0.8rem',
              mb: 1,
              display: 'block',
            }}
          >
            OUR TOP RANKED INSTITUTIONS
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.75rem', md: '2.2rem' },
              mb: 1.5,
            }}
          >
            Premier Medical Colleges
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}
          >
            Handpicked selection of the highest-ranked medical universities worldwide
          </Typography>
        </Box>

        {loading ? (
          /* Loading Skeleton */
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    bgcolor: 'white',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  }}
                >
                  <Box sx={{ height: 180, bgcolor: 'grey.200', animation: 'pulse 1.5s infinite' }} />
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ height: 14, bgcolor: 'grey.200', borderRadius: 1, mb: 2, width: '60%' }} />
                    <Box sx={{ height: 20, bgcolor: 'grey.200', borderRadius: 1, mb: 1, width: '90%' }} />
                    <Box sx={{ height: 14, bgcolor: 'grey.200', borderRadius: 1, width: '40%' }} />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : colleges.length === 0 ? (
          /* Empty State */
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <School sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No top colleges listed yet
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Top medical colleges will appear here once added by the admin.
            </Typography>
          </Box>
        ) : (
          /* College Cards - Horizontal Scroll */
          <Box sx={{ position: 'relative', px: { xs: 0, md: 4 } }}>
            {/* Left Scroll Button */}
            <IconButton
              onClick={() => scroll('left')}
              sx={{
                position: 'absolute',
                left: { xs: -4, md: 0 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: 44,
                height: 44,
                bgcolor: 'white',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                },
                display: { xs: 'none', md: 'flex' },
              }}
            >
              <ChevronLeft />
            </IconButton>

            {/* Scrollable Container */}
            <Box
              ref={scrollContainerRef}
              sx={{
                display: 'flex',
                gap: 3,
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                px: { xs: 0.5, md: 1 },
                py: 1,
                '&::-webkit-scrollbar': {
                  height: 6,
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  borderRadius: 3,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.25),
                  },
                },
              }}
            >
              {colleges.map((college) => {
                // Get country flag for display
                const countryFlag = {
                  'India': '🇮🇳',
                  'Bangladesh': '🇧🇩',
                  'Nepal': '🇳🇵',
                  'Russia': '🇷🇺',
                  'China': '🇨🇳',
                  'Ukraine': '🇺🇦',
                  'Philippines': '🇵🇭',
                  'Kazakhstan': '🇰🇿',
                  'Kyrgyzstan': '🇰🇬',
                  'Uzbekistan': '🇺🇿',
                  'Georgia': '🇬🇪',
                }[college.country] || '🏛️';

                return (
                  <Card
                    key={college._id}
                    sx={{
                      minWidth: 320,
                      maxWidth: 320,
                      flexShrink: 0,
                      borderRadius: 3,
                      overflow: 'hidden',
                      bgcolor: 'white',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        '& .college-image': {
                          transform: 'scale(1.08)',
                        },
                        '& .college-overlay': {
                          opacity: 1,
                        },
                        '& .college-actions': {
                          transform: 'translateY(0)',
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    {/* Image Section */}
                    <Box sx={{ position: 'relative', overflow: 'hidden', height: 180 }}>
                      <CardMedia
                        component="img"
                        className="college-image"
                        height="180"
                        image={getCollegeImage(college)}
                        alt={college.name}
                        sx={{
                          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      />
                      {/* Gradient Overlay */}
                      <Box
                        className="college-overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `linear-gradient(180deg, transparent 40%, ${alpha(theme.palette.primary.dark, 0.7)} 100%)`,
                          opacity: 0,
                          transition: 'opacity 0.4s ease',
                        }}
                      />

                      {/* Top Badges */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          display: 'flex',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Chip
                          icon={<Star sx={{ fontSize: 14 }} />}
                          label="Top College"
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.secondary.main, 0.9),
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            backdropFilter: 'blur(4px)',
                            '& .MuiChip-icon': { color: 'white', ml: 0.5 },
                          }}
                        />
                        {college.ranking > 0 && (
                          <Chip
                            label={`#${college.ranking}`}
                            size="small"
                            sx={{
                              bgcolor: alpha('#FFFFFF', 0.9),
                              color: theme.palette.primary.main,
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              backdropFilter: 'blur(4px)',
                            }}
                          />
                        )}
                      </Box>

                      {/* Country Flag Badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: alpha('#FFFFFF', 0.9),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.2rem',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                      >
                        {countryFlag}
                      </Box>
                    </Box>

                    {/* Content Section */}
                    <CardContent sx={{ p: 3, pb: 1 }}>
                      {/* Location */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationOn
                          sx={{
                            fontSize: 15,
                            mr: 0.5,
                            color: theme.palette.secondary.main,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: alpha(theme.palette.text.secondary, 0.7), fontSize: '0.8rem' }}
                        >
                          {college.city ? `${college.city}, ` : ''}{college.country}
                        </Typography>
                      </Box>

                      {/* College Name */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          lineHeight: 1.3,
                          mb: 1.5,
                          minHeight: 42,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {college.name}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: alpha(theme.palette.text.secondary, 0.8),
                          fontSize: '0.82rem',
                          lineHeight: 1.6,
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: 42,
                        }}
                      >
                        {college.description || 'Leading medical institution offering world-class education and training in healthcare.'}
                      </Typography>

                      {/* College Info Tags */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {college.established && (
                          <Chip
                            label={`Est. ${college.established}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              borderColor: alpha(theme.palette.primary.main, 0.15),
                              color: alpha(theme.palette.text.secondary, 0.8),
                              height: 24,
                            }}
                          />
                        )}
                        {college.country && (
                          <Chip
                            label={college.country}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              borderColor: alpha(theme.palette.primary.main, 0.15),
                              color: alpha(theme.palette.text.secondary, 0.8),
                              height: 24,
                            }}
                          />
                        )}
                      </Box>
                    </CardContent>

                    {/* Actions */}
                    <CardActions
                      className="college-actions"
                      sx={{
                        p: 3,
                        pt: 0,
                        transform: 'translateY(4px)',
                        opacity: 0.9,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        endIcon={<ArrowForward />}
                        onClick={() => scrollToSection('contact')}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          fontWeight: 600,
                          fontSize: '0.82rem',
                          bgcolor: alpha(theme.palette.primary.main, 0.9),
                          '&:hover': {
                            bgcolor: theme.palette.primary.main,
                            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                          },
                        }}
                      >
                        View Details
                      </Button>
                    </CardActions>
                  </Card>
                );
              })}
            </Box>

            {/* Right Scroll Button */}
            <IconButton
              onClick={() => scroll('right')}
              sx={{
                position: 'absolute',
                right: { xs: -4, md: 0 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: 44,
                height: 44,
                bgcolor: 'white',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                },
                display: { xs: 'none', md: 'flex' },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}

        {/* ===== BOTTOM CTA SECTION ===== */}
        <Box
          sx={{
            mt: 8,
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
            border: `2px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative element */}
          <Box
            sx={{
              position: 'absolute',
              top: -60,
              right: -60,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.06)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -40,
              left: -40,
              width: 150,
              height: 150,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              position: 'relative',
              zIndex: 1,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            Ready to Find Your Dream Medical College?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
              position: 'relative',
              zIndex: 1,
            }}
          >
            Get personalized counselling and step-by-step guidance for admissions to top medical colleges worldwide.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            endIcon={<ArrowForward />}
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                const headerOffset = 120;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
              }
            }}
            sx={{
              px: 5,
              py: 1.5,
              fontWeight: 700,
              borderRadius: 2,
              fontSize: '1rem',
              position: 'relative',
              zIndex: 1,
              boxShadow: `0 8px 24px ${alpha(theme.palette.secondary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 12px 32px ${alpha(theme.palette.secondary.main, 0.4)}`,
                transform: 'translateY(-2px)',
              },
            }}
          >
            Get Free Counselling
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default CollegeShowcase;